import type { SuperHubAPIDefinitionSlice } from './ApiDefinitions';
import type { SuperHubBranchesSlice } from './Branches';
import type { SuperHubDocumentSlice } from './Document';
import type { SuperHubEditorSlice } from './Editor';
import type { SuperHubSidebarSlice } from './Sidebar';
import type { SuperHubVersionSlice } from './Versions';
import type { BranchRepresentationType } from '@readme/api/src/mappings/branch/types';
import type { VersionRepresentationType } from '@readme/api/src/mappings/version/types';
import type { ProjectClientSide } from '@readme/backend/models/project/types';

import { matchPath } from 'react-router-dom';
import { createStore } from 'zustand';
import { devtools } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';

import { projectStore } from '@core/store/Project';

import { SuperHubHashRoutePaths, SuperHubRoutePaths } from '@routes/SuperHub/types';
import type { SuperHubHashRouteParams, SuperHubRouteParams } from '@routes/SuperHub/types';

import { actionLog, createBoundedUseStore, isClient } from '../util';

import { createSuperHubAPIDefinitionSlice } from './ApiDefinitions';
import { createSuperHubBranchesSlice } from './Branches';
import { createSuperHubDocumentSlice } from './Document';
import { createSuperHubEditorSlice } from './Editor';
import { createSuperHubSidebarSlice } from './Sidebar';
import { createSuperHubVersionSlice } from './Versions';
import { createDefaultVersion } from './Versions/defaults';

interface SuperHubStoreState {
  /**
   * Current project version or branch that is in view, e.g. the `1.0` version
   * object or `1.0_my-branch` branch object.
   */
  currentBranch: BranchRepresentationType | VersionRepresentationType;

  /**
   * Default SuperHub options object for the useReadmeApi hook
   */
  defaultApiOptions: {
    useLegacyBaseUrl: boolean;
  };

  /**
   * Whether the current project is an Enterprise project or not.
   */
  enterprise?: {
    /**
     * Whether the current project is a child of an Enterprise group.
     */
    isChild: boolean;
    /**
     * Whether the current project is an Enterprise group.
     */
    isGroup: boolean;
    /**
     * Whether the Single Project Enterprise flag is enabled.
     * (The child inherits this flag from the parent group.)
     */
    isSingleProject: boolean;
  };

  /**
   * Indicates whether SuperHub should allow access to bidirectional sync
   */
  isBidiSync: boolean;

  /**
   * True when pages goes into "edit" mode.
   */
  isEditing: boolean;

  /**
   * Whether or not My Developers panel is expanded (defaults to true).
   */
  isMyDevPanelExpanded: boolean;

  /**
   * Indicates that the store has been initialized to its beginning state via
   * `initialize()` action and lets connected subscribers know that it is ready
   * for consumption.
   * @see initialize
   */
  isReady: boolean;

  /**
   * Indicates whether the current route is on the API definitions list view
   * within the Reference section. Mainly used as a convenience that inspects
   * route segments to determine which reference section view to load.
   */
  isReferenceDefinitionsRoute: boolean;

  /**
   * Indicates whether SuperHub is enabled for the current project. Will be
   * `true` for all user roles including both admins and logged-out users.
   */
  isSuperHub: boolean;

  /** Indicates branch reviews are enabled. */
  isSuperHubBranchReviewsEnabled: boolean;

  /**
   * Indicates this project is a child project of an parent enterprise group
   * that has merge permissions enabled.
   */
  isSuperHubMergePermissionsEnabled: boolean;

  /**
   * Determines which layout to render the SuperHub in.
   * - `default`: Default layout for the SuperHub.
   * - `aside`: Used when comparing two documents side-by-side.
   * - `aside-standalone`: Same as `aside` but without a top nav bar.
   * - `edit`: Used when editing a document.
   * - `my-developers`: Used when viewing My Developers panels.
   * - `settings`: Used when configuring settings. The Hub content is hidden in this layout.
   * - `settings-preview`: Used when configuring settings side-by-side with the Hub content.
   * - `settings-preview-stretch`: Used when configuring settings side-by-side for custom code layouts.
   * - `versions`: Used when managing versions.
   * - `ai`: Used when accessing AI features.
   */
  layout:
    | 'ai'
    | 'aside-standalone'
    | 'aside'
    | 'default'
    | 'edit'
    | 'my-developers'
    | 'settings-preview-stretch'
    | 'settings-preview'
    | 'settings'
    | 'versions';

  /**
   * Refers to the top-level route action that is being performed on any given
   * section. For example, when navigating to `/update/reference/pet`, the
   * action is `update`, which indicates we are editing a Reference page with the
   * slug named `pet`.
   */
  routeAction: NonNullable<SuperHubRouteParams['action']> | null;

  /**
   * Refers to the top-level route section that is being viewed on the Hub such
   * as `docs`, `reference`, `changelog`, etc. For example. when navigating to
   * `/reference/pet`, the top-level route section is `reference`. This is
   * typically but not always the first segment of the route URL.
   */
  routeSection: NonNullable<SuperHubRouteParams['section']> | null;

  /**
   * Whether or not preview mode is enabled; and whether it should be expanded
   * (1440px aka container) or fit to the available space. Defaults to `fit`.
   */
  settingsPreviewMode?: 'expand' | 'fit';

  /**
   * Contains the page slug from the hub route that is currently being viewed.
   * For example, when viewing `/docs/getting-started`, the slug is
   * `getting-started`, typically the second segment of the hub route.
   */
  slug: string | null;

  /**
   * Project subdomain that is currently in view, e.g. `my-project-slug`
   */
  subdomain: string | null;

  /** Info about the current user whether they are logged in or not. */
  user: {
    /** Logged in user's ID, e.g. `user.teammateUserId`. */
    id: string | null;
    /** Logged in user's name, e.g. `Banjo Stevens`. */
    name: string | null;
    /**
     * Indicates the current user's role and what features they have access to.
     * - `admin`: full access to project settings and content creating.
     * - `editor`: limited access to project settings and content editing.
     * - `viewer`: logged-out users with view-only access to content.
     */
    role: 'admin' | 'editor' | 'viewer';
  };
}

interface SuperHubStoreAction {
  /**
   * Generate the API endpoint URL with the provided `subdomain` and/or `branch`
   * name. If no argument is provided, it defaults to values that currently
   * exist in the store.
   *
   * To exclude the branch name, set `branch` to an empty string.
   * To exclude the entire branch segment from the URL, set `branch` to `null`.
   *
   * @example
   * ```ts
   * getApiBaseUrl()
   *   => '/subdomain/api-next/v2/branches/1.0'
   * getApiBaseUrl({ subdomain: 'custom', branch: '2.0_branch' })
   *   => '/custom/api-next/v2/branches/2.0_branch'
   * getApiBaseUrl({ branch: '' })
   *   => '/subdomain/api-next/v2/branches'
   * getApiBaseUrl({ branch: null })
   *   => '/subdomain/api-next/v2'
   * ```
   */
  getApiBaseUrl: (args?: {
    subdomain?: SuperHubStoreState['subdomain'];
    branch?: SuperHubStoreState['currentBranch']['name'] | null;
  }) => string;

  /**
   * Initializes the store based on the provided settings. In order to enable
   * SSR, this needs to be called inline and high up in the rendering tree to
   * make the store's state ready before rendering components downstream during
   * both SSR and the initial CSR.
   */
  initialize: (settings: {
    /**
     * The full git-backed representation of the current version or branch. This
     * value is only available for git-backed projects and will be `undefined`
     * for legacy projects.
     */
    currentBranch?: SuperHubStoreState['currentBranch'];

    /** Whether the current project is a child of an Enterprise group. */
    isEnterpriseChild?: boolean;

    /** Whether the current project is an Enterprise group. */
    isEnterpriseGroup?: boolean;

    /**
     * Browser location pathname and hash to initialize the store to.
     * @example
     * ['/docs/getting-started', '#/configure/appearance/theme-editor']
     */
    location?: [pathname: string, hash?: string];

    /**
     * Project feature flags that are relevant to SuperHub in order to determine
     * whether it should be enabled or not.
     */
    projectFlags: Partial<ProjectClientSide['flags']>;

    /**
     * Project subdomain.
     * @example my-project
     */
    subdomain: SuperHubStoreState['subdomain'];

    /** Info about the currently logged in user. */
    user?: Pick<SuperHubStoreState['user'], 'id' | 'name' | 'role'>;
  }) => void;

  /**
   * Resets the entire store back to its initial default state.
   */
  reset: () => void;

  /**
   * Toggles the `isMyDevPanelExpanded` state.
   */
  updateIsMyDevPanelExpanded: () => void;

  /**
   * Updates the current route we've currently navigated to with the provided
   * URL pathname and hash properties. Segments in each property are then parsed
   * and interpreted by our state.
   * @see ConnectSuperHubStoreToRouter
   */
  updateRoute: (pathname: string, hash?: string) => void;

  /**
   * Toggles the `preview` state.
   */
  updateSettingsPreviewMode: (settingsPreviewMode?: 'expand' | 'fit') => void;
}

export type SuperHubStore = SuperHubAPIDefinitionSlice &
  SuperHubBranchesSlice &
  SuperHubDocumentSlice &
  SuperHubEditorSlice &
  SuperHubSidebarSlice &
  SuperHubStoreAction &
  SuperHubStoreState &
  SuperHubVersionSlice;

const initialState: SuperHubStoreState = {
  currentBranch: {
    // Default our initial branch to `stable` to cover cases like for enterprise
    // groups where they are technically version-less.
    ...createDefaultVersion({ name: 'stable' }),
    source: 'readme',
    updated_at: '',
    uri: '/branches/stable',
  },
  defaultApiOptions: {
    useLegacyBaseUrl: false,
  },
  isBidiSync: false,
  isEditing: false,
  isMyDevPanelExpanded: true,
  isReady: false,
  isReferenceDefinitionsRoute: false,
  isSuperHub: false,
  isSuperHubBranchReviewsEnabled: false,
  isSuperHubMergePermissionsEnabled: false,
  layout: 'default',
  routeAction: null,
  routeSection: null,
  settingsPreviewMode: 'fit',
  slug: null,
  subdomain: null,
  user: { id: null, name: null, role: 'viewer' },
};

/**
 * Vanilla store that contains all application-level state data required by
 * SuperHub. This store can be accessed and used anywhere. React components
 * should call `useSuperHubStore()` instead.
 * @example
 * import { superHubStore } from '@core/store';
 *
 * const isEditing = superHubStore.getState().isEditing;
 */
export const superHubStore = createStore<SuperHubStore>()(
  devtools(
    immer((set, get, ...props) => {
      /**
       * Holds reference to the initial state so we can support resetting the
       * store back to this state when calling `reset()`.
       */
      const resetState = {
        ...createSuperHubAPIDefinitionSlice(set, get, ...props),
        ...createSuperHubDocumentSlice(set, get, ...props),
        ...createSuperHubEditorSlice(set, get, ...props),
        ...createSuperHubSidebarSlice(set, get, ...props),
        ...createSuperHubVersionSlice(set, get, ...props),
        ...createSuperHubBranchesSlice(set, get, ...props),
        ...initialState,
      };

      return {
        ...resetState,

        getApiBaseUrl: (args = {}) => {
          const { subdomain: currentSubdomain, currentBranch } = get();
          const subdomain = args.subdomain || currentSubdomain;
          if (args.branch === null) {
            return `/${subdomain}/api-next/v2`;
          } else if (args.branch === '') {
            return `/${subdomain}/api-next/v2/branches`;
          }

          const branch = args.branch || currentBranch.name;
          return `/${subdomain}/api-next/v2/branches/${encodeURIComponent(branch)}`;
        },

        initialize: settings => {
          // When store is initializing during SSR, we have to explicitly reset
          // it to its initial state. Otherwise, there could be remnants of
          // stale data leftover in the store from a previous SSR. This is b/c
          // our store is a singleton and never gets destroyed on the server.
          if (!isClient) get().reset();

          const {
            currentBranch,
            isEnterpriseChild,
            isEnterpriseGroup,
            location = [],
            projectFlags: flags,
            subdomain,
            user,
          } = settings;
          set(
            state => {
              if (currentBranch) {
                state.currentBranch = currentBranch;
              }

              state.isReady = isClient;
              state.isSuperHub = !!flags.superHub;
              state.isSuperHubBranchReviewsEnabled = state.isSuperHub && !!flags.superHubBranchReviews;
              state.isSuperHubMergePermissionsEnabled = state.isSuperHub && !!flags.superHubMergePermissions;
              state.isBidiSync = state.isSuperHub && user?.role === 'admin' && !!flags.bidiSync;
              state.subdomain = subdomain;
              state.user = {
                id: user?.id || null,
                name: user?.name || null,
                role: state.isSuperHub && user ? user.role : 'viewer',
              };

              if (isEnterpriseGroup || isEnterpriseChild) {
                state.enterprise = {
                  isGroup: !!isEnterpriseGroup,
                  isChild: !!isEnterpriseChild,
                  isSingleProject: 'singleProjectEnterprise' in flags && !!flags.singleProjectEnterprise,
                };
              }
            },
            false,
            actionLog('initialize', settings),
          );

          // Continue initialization based on the location route.
          const [pathname, hash] = location;
          if (pathname) {
            get().updateRoute(pathname, hash);
          }
        },

        reset: () => {
          set(resetState, false, actionLog('reset'));
          get().sidebar.updateCache();
        },

        updateIsMyDevPanelExpanded: () => {
          set(
            state => {
              state.isMyDevPanelExpanded = !state.isMyDevPanelExpanded;
            },
            false,
            actionLog('updateIsMyDevPanelExpanded'),
          );
        },

        updateSettingsPreviewMode: (settingsPreviewMode = 'fit') => {
          set(
            state => {
              state.settingsPreviewMode = settingsPreviewMode;
            },
            false,
            actionLog('updateSettingsPreviewMode', { settingsPreviewMode }),
          );
        },

        updateRoute: (pathname, hash = '') => {
          // Match the same route paths from our superhub routers to parse out
          // route params in the same way.
          const match = matchPath<SuperHubRouteParams>(pathname, Object.values(SuperHubRoutePaths));
          const hashMatch = matchPath<SuperHubHashRouteParams>(
            hash.substring(1),
            Object.values(SuperHubHashRoutePaths),
          );

          const {
            params: { action = null, section = pathname === '/' ? 'home' : null, slug = null },
          } = match ?? { params: {} };
          const {
            params: { action: hashAction, section: hashSection },
          } = hashMatch ?? { params: {} };

          set(
            state => {
              state.isEditing = ['create', 'update', 'compare'].includes(action || '');
              state.isReferenceDefinitionsRoute = action === 'update' && section === 'reference' && !slug;
              state.routeAction = action;
              state.routeSection = section;
              state.slug = slug;
              state.editor.isCreateNewPage = action === 'create';

              // ⚠️ Be mindful of order when updating!! Hash actions should be
              // evaluated before actions, to ensure that hash action based
              // panel overlays take precedense over the main action
              if (action === 'compare') {
                state.layout = 'aside-standalone';
              } else if (hashAction === 'versions') {
                state.layout = 'versions';
              } else if (hashAction === 'my-developers') {
                state.layout = 'my-developers';
              } else if (hashAction === 'configure' || hashAction === 'content') {
                state.layout = 'settings';
              } else if (hashAction === 'appearance' && hashSection?.includes('custom-code')) {
                state.layout = 'settings-preview-stretch';
              } else if (hashAction === 'appearance') {
                state.layout = 'settings-preview';
              } else if (hashAction === 'ai') {
                state.layout = 'ai';
              } else if (action === 'create') {
                state.layout = 'edit';
              } else if (action === 'update') {
                state.layout = 'edit';
              } else {
                state.layout = 'default';
              }

              if (['configure', 'content', 'appearance', 'my-developers', 'ai'].includes(hashAction ?? '')) {
                // Always clear project store errors when re-routing.
                projectStore.getState().resetSaveError();
              }
            },
            false,
            actionLog('updateRoute', { pathname, hash }),
          );
        },
      };
    }),
    { name: 'SuperHubStore' },
  ),
);

/**
 * Bound react hook to access our SuperHub store. Must be called within a React
 * component. To access the store outside of React, use `superHubStore` instead.
 * @example
 * import { useSuperHubStore } from '@core/store';
 *
 * function Component() {
 *   const isEditing = useSuperHubStore(s => s.isEditing);
 * }
 */
export const useSuperHubStore = createBoundedUseStore(superHubStore);

export * from './ApiDefinitions';
export * from './Branches';
export * from './ConnectSuperHubStoreToRouter';
export * from './Document';
export * from './Editor';
export * from './InitializeSuperHubStore';
export * from './Sidebar';
export * from './Versions';
